"use client";

/**
 * P9-12 · Mobile bottom tab bar (P9-DB02). Thumb-reachable Home / Search /
 * Wishlist / Cart / Account with live cart + wishlist badges. Mobile-only
 * (hidden ≥ md), safe-area aware, `aria-current` on the active tab. Yields the
 * bottom slot to the sticky ATC on the PDP and the cart-summary bar on /cart
 * ("one fixed bar at a time"). Tab set is config-driven (wishlist tab gated).
 */
import * as React from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { useLocaleHref } from "~/i18n/provider";
import { track } from "~/lib/analytics/client";
import { useCartCount } from "~/lib/cart/use-cart-count";
import { useWishlistCount } from "~/lib/wishlist/use-wishlist";
import {
  isTabActive,
  navHiddenOnRoute,
  resolveTabs,
  type TabKey,
} from "./bottom-nav-tabs";

function Icon({ tab, active }: { tab: TabKey; active: boolean }) {
  const stroke = active ? 2.1 : 1.7;
  const common = {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  switch (tab) {
    case "home":
      return (
        <svg {...common}>
          <path d="M3 10.5 12 3l9 7.5" />
          <path d="M5 9.5V21h14V9.5" />
        </svg>
      );
    case "search":
      return (
        <svg {...common}>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.2-3.2" />
        </svg>
      );
    case "wishlist":
      return (
        <svg {...common}>
          <path d="M12 21s-7-4.6-9.3-9C1.3 9.2 2.6 6 5.8 6 8 6 9.4 7.6 12 10c2.6-2.4 4-4 6.2-4 3.2 0 4.5 3.2 3.1 6-2.3 4.4-9.3 9-9.3 9Z" />
        </svg>
      );
    case "cart":
      return (
        <svg {...common}>
          <path d="M6 7h12l-1 12H7L6 7Z" />
          <path d="M9 7a3 3 0 0 1 6 0" />
        </svg>
      );
    case "account":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="4" />
          <path d="M4 20a8 8 0 0 1 16 0" />
        </svg>
      );
  }
}

export function BottomNav({
  signedIn,
  wishlistEnabled,
}: {
  signedIn: boolean;
  wishlistEnabled: boolean;
}) {
  const pathname = usePathname();
  const localeHref = useLocaleHref();
  const cartCount = useCartCount();
  const wishlistCount = useWishlistCount({
    signedIn,
    enabled: wishlistEnabled,
  });

  if (navHiddenOnRoute(pathname)) return null;
  const tabs = resolveTabs({ wishlistEnabled });

  return (
    <nav
      aria-label="Primary"
      className="fixed inset-x-0 bottom-0 z-40 grid border-t border-border bg-surface/95 backdrop-blur md:hidden [padding-bottom:env(safe-area-inset-bottom)]"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((tab) => {
        const active = isTabActive(tab, pathname);
        const count =
          tab.badge === "cart"
            ? cartCount
            : tab.badge === "wishlist"
              ? wishlistCount
              : 0;
        return (
          <Link
            key={tab.key}
            href={localeHref(tab.href)}
            aria-current={active ? "page" : undefined}
            onClick={() => track("PAGE_VIEW", { props: { navTab: tab.key } })}
            className={`relative flex min-h-[52px] flex-col items-center justify-center gap-0.5 text-[11px] ${
              active ? "font-semibold text-fg" : "text-fg-muted"
            }`}
          >
            <Icon tab={tab.key} active={active} />
            <span>{tab.label}</span>
            {tab.badge && count > 0 ? (
              <span
                aria-label={`${count} ${tab.badge === "cart" ? "items in cart" : "saved items"}`}
                className="absolute right-[calc(50%-1.4rem)] top-1.5 min-w-[1.1rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-[1.1rem] text-primary-fg"
              >
                {count > 99 ? "99+" : count}
              </span>
            ) : null}
          </Link>
        );
      })}
    </nav>
  );
}
